import { useState, useMemo, useEffect, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import {
  TrendingUp, TrendingDown, Filter, Search, RefreshCw, AlertTriangle,
  Users, Shield, Activity, FileText, MessageCircle, Target, Zap,
  ArrowUpDown, Eye, EyeOff, Settings, BarChart3, Clock, ChevronDown,
  HelpCircle, Plus, Sparkles, Brain, TrendingUpIcon
} from 'lucide-react';
import { fetchWatchlistThunk, unfollowStockThunk } from '../../redux/user/watchlist/watchlistActions';
import { fetchBatchSnapshots } from '../../redux/stockData/marketSnapshot/marketSnapshotActions';
import { selectSentimentByTicker, selectSentimentLoading } from '../../redux/sentimentAggregator/sentimentAggSlice';
import { fetchStockSentimentAggsThunk } from '../../redux/sentimentAggregator/sentimentAggActionThunks';
import StockLogo from '../StockLogo/StockLogoRender';

const sentimentColors = {
  bullish: {
    color: "text-emerald-700 dark:text-emerald-400",
    bg: "bg-emerald-50 border-emerald-300 dark:bg-emerald-900/30 dark:border-emerald-600/60",
    icon: TrendingUp,
  },
  bearish: {
    color: "text-red-700 dark:text-red-400",
    bg: "bg-red-50 border-red-300 dark:bg-red-900/30 dark:border-red-600/60",
    icon: TrendingDown,
  },
  neutral: {
    color: "text-gray-600 dark:text-gray-400",
    bg: "bg-gray-100 border-gray-300 dark:bg-gray-800/60 dark:border-gray-600/60",
    icon: ArrowUpDown,
  },
};

// Label/description strings are looked up via t() at the call sites below
// (getSectionConfig/getTimeframeOptions) since this file is module scope and
// has no hook access -- the lookup keys themselves (retail, credible, noise,
// groups_membership, groups_posts, news) are left untouched since they're
// matched against backend response keys elsewhere in this file.
const getSectionConfig = (t) => ({
  retail: {
    label: t('watchlist.sections.retail.label'),
    icon: Users,
    color: "text-blue-600 dark:text-blue-400",
    weight: 0.2,
    description: t('watchlist.sections.retail.description')
  },
  credible: {
    label: t('watchlist.sections.credible.label'),
    icon: Shield,
    color: "text-purple-600 dark:text-purple-400",
    weight: 0.5,
    description: t('watchlist.sections.credible.description')
  },
  noise: {
    label: t('watchlist.sections.noise.label'),
    icon: Activity,
    color: "text-yellow-700 dark:text-yellow-400",
    weight: 0.05,
    description: t('watchlist.sections.noise.description')
  },
  groups_membership: {
    label: t('watchlist.sections.groups_membership.label'),
    icon: Users,
    color: "text-cyan-700 dark:text-cyan-400",
    weight: 0.15,
    description: t('watchlist.sections.groups_membership.description')
  },
  groups_posts: {
    label: t('watchlist.sections.groups_posts.label'),
    icon: MessageCircle,
    color: "text-pink-600 dark:text-pink-400",
    weight: 0.1,
    description: t('watchlist.sections.groups_posts.description')
  },
  news: {
    label: t('watchlist.sections.news.label'),
    icon: FileText,
    color: "text-blue-600 dark:text-blue-400",
    weight: 0.2,
    description: t('watchlist.sections.news.description')
  },
});

const getTimeframeOptions = (t) => [
  { value: "24h", label: t('watchlist.timeframes.24h.label'), description: t('watchlist.timeframes.24h.description') },
  { value: "7d", label: t('watchlist.timeframes.7d.label'), description: t('watchlist.timeframes.7d.description') },
  { value: "1m", label: t('watchlist.timeframes.1m.label'), description: t('watchlist.timeframes.1m.description') },
  { value: "3m", label: t('watchlist.timeframes.3m.label'), description: t('watchlist.timeframes.3m.description') },
  { value: "1y", label: t('watchlist.timeframes.1y.label'), description: t('watchlist.timeframes.1y.description') },
];

const ConfidenceMeter = ({ score, sampleSize, className = "" }) => {
  const { t } = useTranslation();
  const getConfidenceLevel = (size) => {
    if (size >= 100) return { level: t('watchlist.confidence.high'), color: "text-emerald-700 dark:text-emerald-400", bg: "bg-emerald-500" };
    if (size >= 25) return { level: t('watchlist.confidence.medium'), color: "text-yellow-700 dark:text-yellow-400", bg: "bg-yellow-500" };
    return { level: t('watchlist.confidence.low'), color: "text-red-600 dark:text-red-400", bg: "bg-red-500" };
  };

  const confidence = getConfidenceLevel(sampleSize);

  return (
    <div className={`flex items-center gap-1 ${className}`}>
      <div className="w-8 bg-gray-200 dark:bg-gray-700 rounded-full h-1">
        <div 
          className={`h-full rounded-full transition-all ${confidence.bg}`}
          style={{ width: `${Math.min(score * 100, 100)}%` }}
        />
      </div>
      <span className={`text-xs ${confidence.color}`}>{confidence.level}</span>
    </div>
  );
};

const EmptyWatchlistState = ({ onExploreStocks }) => {
  const { t } = useTranslation();
  return (
    <div className="relative overflow-hidden">
      <div className="relative bg-gradient-to-br from-gray-50 via-white to-gray-50 border border-gray-200 dark:from-slate-800/40 dark:via-slate-900/60 dark:to-slate-800/40 dark:border-slate-600/30 backdrop-blur-xl rounded-2xl p-8">
        <div className="absolute inset-0 overflow-hidden rounded-2xl">
          <div className="absolute -top-4 -right-4 w-24 h-24 bg-indigo-100 dark:bg-indigo-500/10 rounded-full blur-xl animate-pulse"></div>
          <div className="absolute -bottom-4 -left-4 w-32 h-32 bg-purple-100 dark:bg-purple-500/10 rounded-full blur-xl animate-pulse delay-1000"></div>
          <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-40 h-40 bg-emerald-500/5 rounded-full blur-2xl animate-pulse delay-500"></div>
        </div>
        
        <div className="relative z-10 text-center space-y-6">
          <div className="flex justify-center">
            <div className="relative">
              <div className="absolute inset-0 bg-indigo-500/20 rounded-full blur-lg animate-pulse"></div>
              <div className="relative w-16 h-16 bg-gradient-to-br from-violet-500/20 to-indigo-500/20 backdrop-blur-sm border border-indigo-400/30 rounded-full flex items-center justify-center">
                <Brain className="w-8 h-8 text-indigo-600 dark:text-indigo-400" />
              </div>
            </div>
          </div>

          <div className="space-y-2">
            <h3 className="text-2xl font-bold bg-gradient-to-r from-gray-900 via-gray-800 to-gray-600 dark:from-white dark:via-gray-100 dark:to-gray-300 bg-clip-text text-transparent">
              {t('watchlist.emptyState.title')}
            </h3>
            <p className="text-gray-600 dark:text-gray-400 text-sm max-w-md mx-auto leading-relaxed">
              {t('watchlist.emptyState.description')}
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 py-4">
            <div className="flex flex-col items-center space-y-2">
              <div className="w-10 h-10 bg-gradient-to-br from-emerald-500/20 to-emerald-600/20 backdrop-blur-sm border border-emerald-400/30 rounded-lg flex items-center justify-center">
                <Shield className="w-5 h-5 text-emerald-700 dark:text-emerald-400" />
              </div>
              <div className="text-center">
                <p className="text-sm font-medium text-gray-900 dark:text-white">{t('watchlist.emptyState.credibleSources.title')}</p>
                <p className="text-xs text-gray-500">{t('watchlist.emptyState.credibleSources.subtitle')}</p>
              </div>
            </div>

            <div className="flex flex-col items-center space-y-2">
              <div className="w-10 h-10 bg-gradient-to-br from-indigo-500/20 to-indigo-600/20 backdrop-blur-sm border border-indigo-400/30 rounded-lg flex items-center justify-center">
                <BarChart3 className="w-5 h-5 text-indigo-600 dark:text-indigo-400" />
              </div>
              <div className="text-center">
                <p className="text-sm font-medium text-gray-900 dark:text-white">{t('watchlist.emptyState.realTimeData.title')}</p>
                <p className="text-xs text-gray-500">{t('watchlist.emptyState.realTimeData.subtitle')}</p>
              </div>
            </div>

            <div className="flex flex-col items-center space-y-2">
              <div className="w-10 h-10 bg-gradient-to-br from-purple-500/20 to-purple-600/20 backdrop-blur-sm border border-purple-400/30 rounded-lg flex items-center justify-center">
                <Sparkles className="w-5 h-5 text-purple-600 dark:text-purple-400" />
              </div>
              <div className="text-center">
                <p className="text-sm font-medium text-gray-900 dark:text-white">{t('watchlist.emptyState.aiInsights.title')}</p>
                <p className="text-xs text-gray-500">{t('watchlist.emptyState.aiInsights.subtitle')}</p>
              </div>
            </div>
          </div>

          <div className="space-y-3 pt-2">
            <p className="text-sm text-gray-700 dark:text-gray-300 font-medium">{t('watchlist.emptyState.startPrompt')}</p>

            <button
              onClick={onExploreStocks}
              className="group relative inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-violet-600 to-indigo-600 hover:from-violet-500 hover:to-indigo-500 text-white font-medium rounded-xl transition-all duration-200 transform hover:scale-105 shadow-lg hover:shadow-xl"
            >
              <div className="absolute inset-0 bg-gradient-to-r from-violet-600/50 to-indigo-600/50 rounded-xl blur opacity-75 group-hover:opacity-100 transition-opacity"></div>
              <div className="relative flex items-center gap-2">
                <Plus className="w-4 h-4" />
                <span>{t('watchlist.emptyState.exploreStocks')}</span>
                <TrendingUpIcon className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" />
              </div>
            </button>

            <p className="text-xs text-gray-500 max-w-xs mx-auto">
              {t('watchlist.emptyState.footerNote')}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

const StockCard = ({ stock, timeframe, isExpanded, onToggleExpanded, onRemove }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const sectionConfig = useMemo(() => getSectionConfig(t), [t]);

  const sentimentData = useSelector((state) =>
    selectSentimentByTicker(state, stock.symbol, timeframe)
  );
  const sentimentLoading = useSelector((state) =>
    selectSentimentLoading(state, stock.symbol, timeframe)
  );

  // Market data
  const batchSnapshots = useSelector(state => state.marketSnapshot.batchSnapshots || []);
  const marketData = batchSnapshots.find(snap => snap.ticker === stock.symbol) || {};

  // Fetch sentiment data when component mounts or timeframe changes - but only if needed
  useEffect(() => {
    if (stock.symbol && !sentimentData && !sentimentLoading) {
      dispatch(fetchStockSentimentAggsThunk({ ticker: stock.symbol, timeframe }));
    }
  }, [stock.symbol, timeframe, sentimentData, sentimentLoading, dispatch]);

  const analysis = useMemo(() => {
    if (sentimentLoading) return { loading: true };
    if (!sentimentData) return null;

    // Calculate weighted sentiment score
    let weightedBullishScore = 0;
    let totalWeight = 0;
    let totalVotes = 0;
    let totalBullish = 0;
    let totalBearish = 0;
    let totalArticles = 0;

    const sections = Object.entries(sectionConfig)
      .map(([key, config]) => {
        const data = sentimentData[key];
        if (!data) {
          return {
            key,
            ...config,
            data: null,
            sentiment: 'neutral',
            sentimentConfig: sentimentColors.neutral,
            bullishRatio: 0,
            sampleSize: 0,
          };
        }
        
        const sectionSentiment = data.combined_sentiment || 'neutral';
        const sentimentConfig = sentimentColors[sectionSentiment] || sentimentColors.neutral;
        
        if (data.votes) {
          const bullishRatio = data.votes.bullish_ratio || 0;
          weightedBullishScore += bullishRatio * config.weight;
          totalWeight += config.weight;
          totalVotes += data.votes.total_votes || 0;
          totalBullish += data.votes.bullish_count || 0;
          totalBearish += data.votes.bearish_count || 0;
        }
        
        if (data.news) {
          totalArticles += data.news.articles_analyzed || 0;
        }
        
        return {
          key,
          ...config,
          data,
          sentiment: sectionSentiment,
          sentimentConfig,
          bullishRatio: data.votes?.bullish_ratio || 0,
          sampleSize: data.votes?.total_votes || 0,
        };
      });

    // Calculate overall weighted sentiment
    const weightedSentiment = totalWeight > 0 ? weightedBullishScore / totalWeight : 0;
    let overallSentiment = 'neutral';
    if (weightedSentiment > 0.6) overallSentiment = 'bullish';
    else if (weightedSentiment < 0.4) overallSentiment = 'bearish';
    
    const sentimentConfig = sentimentColors[overallSentiment];
    
    // Calculate confidence based on credible sample size
    const credibleSection = sections.find(s => s.key === 'credible');
    const confidenceScore = credibleSection ? Math.min(credibleSection.sampleSize / 100, 1) : 0;

    return {
      overallSentiment,
      sentimentConfig,
      weightedSentiment,
      confidenceScore,
      totalVotes,
      totalBullish,
      totalBearish,
      totalArticles,
      sections,
      lastUpdated: sentimentData.last_updated,
    };
  }, [sentimentData, sentimentLoading, sectionConfig]);

  const formatNumber = (value, digits = 2) =>
    Number(value ?? 0).toFixed(digits);
  const price = marketData.last_price ? `$${marketData.last_price}` : '—';
  const change = marketData.change ?? 0;
  const percentChange = marketData.change_percent ?? 0;
  
  const isPositive = Number(change) > 0;
  const changeColor = Number(change) === 0 ? 'text-gray-500 dark:text-gray-400' :
                     isPositive ? 'text-emerald-700 dark:text-emerald-400' : 'text-red-600 dark:text-red-400';

  if (analysis?.loading) {
    return (
      <div className="bg-gray-100 dark:bg-slate-800/60 backdrop-blur-sm border border-gray-200 dark:border-slate-600/50 rounded-xl p-4">
        <div className="animate-pulse space-y-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-gray-300 dark:bg-slate-700/70 rounded-lg"></div>
              <div className="space-y-2">
                <div className="h-3 bg-gray-300 dark:bg-slate-700/70 rounded w-16"></div>
                <div className="h-2 bg-gray-200 dark:bg-slate-700/70 rounded w-24"></div>
              </div>
            </div>
            <div className="space-y-2 text-right">
              <div className="h-3 bg-gray-300 dark:bg-slate-700/70 rounded w-20"></div>
              <div className="h-2 bg-gray-200 dark:bg-slate-700/70 rounded w-16"></div>
            </div>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div className="h-12 bg-gray-200 dark:bg-slate-700/50 rounded-lg"></div>
            <div className="h-12 bg-gray-200 dark:bg-slate-700/50 rounded-lg"></div>
            <div className="h-12 bg-gray-200 dark:bg-slate-700/50 rounded-lg"></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-gray-50 dark:bg-slate-800/60 backdrop-blur-sm border border-gray-200 dark:border-slate-600/50 rounded-xl p-4 hover:bg-gray-100 dark:hover:bg-slate-700/50 transition-all duration-200 hover:border-gray-300 dark:hover:border-slate-500/60">
      {/* Header */}
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center gap-3">
          <StockLogo symbol={stock.symbol} name={stock.name} size="w-10 h-10" />
          <div>
            <h3 className="font-bold text-gray-900 dark:text-white text-sm">{stock.symbol}</h3>
            <p className="text-xs text-gray-500 dark:text-gray-400 max-w-[140px] truncate">{stock.name}</p>
          </div>
        </div>

        <div className="text-right">
          <p className="font-bold text-gray-900 dark:text-white text-sm">{price}</p>
          <p className={`text-xs flex items-center justify-end gap-1 ${changeColor}`}>
            {isPositive ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
            {Number(change) > 0 ? '+' : ''}{formatNumber(change)} ({formatNumber(percentChange)}%)
          </p>
        </div>
      </div>
      
      {/* Sentiment Overview */}
      {analysis && (
        <>
          <div className="flex items-center justify-between mb-3 pb-3 border-b border-gray-200 dark:border-slate-600/50">
            <div className={`flex items-center gap-2 px-3 py-1.5 rounded-full border ${analysis.sentimentConfig.bg}
              backdrop-blur-md shadow-md transition-all`}>
              <analysis.sentimentConfig.icon className={`w-4 h-4 ${analysis.sentimentConfig.color}`} />
              <span className={`text-xs font-semibold tracking-wide ${analysis.sentimentConfig.color}`}>
                {t(`watchlist.sentimentValues.${analysis.overallSentiment}`)}
              </span>
            </div>

            <button
              onClick={() => onToggleExpanded(stock.symbol)}
              aria-label={isExpanded ? t('watchlist.stockCard.collapseDetails', { symbol: stock.symbol }) : t('watchlist.stockCard.expandDetails', { symbol: stock.symbol })}
              className="text-gray-500 hover:text-gray-900 dark:text-gray-400 dark:hover:text-white transition-colors p-1 rounded-md hover:bg-gray-200 dark:hover:bg-slate-600/30"
            >
              <ChevronDown className={`w-4 h-4 transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
            </button>
          </div>

          {/* Bullish/Bearish Distribution Chips */}
          <div className="flex gap-2 mb-3">
            <div className="flex items-center gap-1 px-3 py-1 rounded-full bg-emerald-50 border border-emerald-300 dark:bg-emerald-500/10 dark:border-emerald-500/30 backdrop-blur-md shadow-sm">
              <TrendingUp className="w-3 h-3 text-emerald-700 dark:text-emerald-400" />
              <span className="text-xs text-emerald-700 dark:text-emerald-300 font-medium">
                {t('watchlist.stockCard.bullishPct', { pct: Math.round(analysis.weightedSentiment * 100) })}
              </span>
            </div>
            <div className="flex items-center gap-1 px-3 py-1 rounded-full bg-red-50 border border-red-300 dark:bg-red-500/10 dark:border-red-500/30 backdrop-blur-md shadow-sm">
              <TrendingDown className="w-3 h-3 text-red-600 dark:text-red-400" />
              <span className="text-xs text-red-700 dark:text-red-300 font-medium">
                {t('watchlist.stockCard.bearishPct', { pct: 100 - Math.round(analysis.weightedSentiment * 100) })}
              </span>
            </div>
          </div>

          {/* Quick Stats as Chips */}
          <div className="flex flex-wrap gap-2 mb-3">
            <div className="flex items-center gap-1 px-3 py-1.5 rounded-full border border-purple-300 bg-purple-50
              dark:border-purple-500/30 dark:bg-purple-500/10 backdrop-blur-md shadow-sm">
              <Shield className="w-3 h-3 text-purple-600 dark:text-purple-400" />
              <span className="text-xs text-purple-700 dark:text-purple-300 font-medium">
                {t('watchlist.stockCard.credibleLabel', { sentiment: analysis.sections.find(s => s.key === 'credible')?.sentiment || 'neutral' })}
              </span>
              <span className="text-[10px] text-gray-500 dark:text-gray-400 ml-1">
                {t('watchlist.stockCard.votesCount', { count: analysis.sections.find(s => s.key === 'credible')?.sampleSize || 0 })}
              </span>
            </div>

            <div className="flex items-center gap-1 px-3 py-1.5 rounded-full border border-blue-300 bg-blue-50
              dark:border-blue-500/30 dark:bg-blue-500/10 backdrop-blur-md shadow-sm">
              <Users className="w-3 h-3 text-blue-600 dark:text-blue-400" />
              <span className="text-xs text-blue-700 dark:text-blue-300 font-medium">
                {t('watchlist.stockCard.retailLabel', { sentiment: analysis.sections.find(s => s.key === 'retail')?.sentiment || 'neutral' })}
              </span>
              <span className="text-[10px] text-gray-500 dark:text-gray-400 ml-1">
                {t('watchlist.stockCard.votesCount', { count: analysis.sections.find(s => s.key === 'retail')?.sampleSize || 0 })}
              </span>
            </div>

            <div className="flex items-center gap-1 px-3 py-1.5 rounded-full border border-cyan-300 bg-cyan-50
              dark:border-cyan-500/30 dark:bg-cyan-500/10 backdrop-blur-md shadow-sm">
              <FileText className="w-3 h-3 text-cyan-700 dark:text-cyan-400" />
              <span className="text-xs text-cyan-700 dark:text-cyan-300 font-medium">
                {t('watchlist.stockCard.articlesCount', { count: analysis.totalArticles })}
              </span>
            </div>
          </div>

          {/* Confidence meter */}
          <div className="flex items-center justify-between mb-3">
            <span className="text-xs text-gray-500 dark:text-gray-400">{t('watchlist.confidence.label')}</span>
            <ConfidenceMeter
              score={analysis.confidenceScore}
              sampleSize={analysis.sections.find(s => s.key === 'credible')?.sampleSize || 0}
            />
          </div>

          {/* Remove Button */}
          <button
            onClick={() => onRemove(stock.id)}
            className="w-full text-xs text-red-600 hover:text-red-700 hover:bg-red-50 dark:text-red-400 dark:hover:text-red-300 dark:hover:bg-red-900/20 py-2 rounded-lg transition-all duration-200"
          >
            {t('common.removeFromWatchlist')}
          </button>
        </>
      )}

      {/* No Sentiment Data */}
      {!analysis && !sentimentLoading && (
        <div className="text-center py-4">
          <div className="w-8 h-8 bg-gray-200 dark:bg-slate-700/50 rounded-lg flex items-center justify-center mx-auto mb-2">
            <HelpCircle className="w-4 h-4 text-gray-500 dark:text-gray-400" />
          </div>
          <p className="text-xs text-gray-500 dark:text-gray-400">{t('watchlist.stockCard.noSentimentData')}</p>
        </div>
      )}
    </div>
  );
};

const SentimentDashboard = ({ userId, onExploreStocks }) => {
  const { t } = useTranslation();
  const timeframeOptions = useMemo(() => getTimeframeOptions(t), [t]);
  const dispatch = useDispatch();
  const [searchTerm, setSearchTerm] = useState('');
  const [sentimentFilter, setSentimentFilter] = useState('all');
  const [sortBy, setSortBy] = useState('symbol');
  const [expandedStocks, setExpandedStocks] = useState(new Set());
  const [timeframe, setTimeframe] = useState('7d');
  const [isRefreshing, setIsRefreshing] = useState(false);
  
  // Use the same watchlist data pattern as other components
  const watchlistItems = useSelector(state => state.watchlist.items ?? state.watchlist.stocks ?? []);
  const watchlistLoading = useSelector(state => state.watchlist.loading);
  const watchlistError = useSelector(state => state.watchlist.error);

  // Market data batch (by symbol)
  const batchSnapshots = useSelector(state => state.marketSnapshot.batchSnapshots || []);
  const batchStatus = useSelector(state => state.marketSnapshot.batchStatus);
  const batchLoading = batchStatus === 'loading';

  // Memoized callback for reloading watchlist
  const reloadWatchlist = useCallback(() => {
    if (userId) {
      dispatch(fetchWatchlistThunk({ skip: 0, limit: 50 }));
    }
  }, [dispatch, userId]);

  // Fetch watchlist on mount - same pattern as watchList.jsx - ONLY if needed
  useEffect(() => {
    if (userId && watchlistItems.length === 0 && !watchlistLoading && !watchlistError) {
      reloadWatchlist();
    }
  }, [userId, watchlistItems.length, watchlistLoading, watchlistError, reloadWatchlist]);

  // Pull symbols, then fetch batch market data when items change - same pattern
  const symbols = useMemo(
    () => watchlistItems.map(s => s.symbol).filter(Boolean),
    [watchlistItems]
  );

  useEffect(() => {
    if (symbols.length > 0 && !batchLoading) {
      // Check which symbols we don't have data for
      const existingTickers = new Set(batchSnapshots.map(snap => snap.ticker));
      const missingSymbols = symbols.filter(symbol => !existingTickers.has(symbol));
      
      // Only fetch if we have missing symbols
      if (missingSymbols.length > 0 || batchSnapshots.length === 0) {
        dispatch(fetchBatchSnapshots(symbols));
      }
    }
  }, [symbols, batchSnapshots, batchLoading, dispatch]);

  const handleToggleExpanded = (symbol) => {
    setExpandedStocks(prev => {
      const newSet = new Set(prev);
      if (newSet.has(symbol)) {
        newSet.delete(symbol);
      } else {
        newSet.add(symbol);
      }
      return newSet;
    });
  };
  
  const handleRefresh = async () => {
    setIsRefreshing(true);
    try {
      await Promise.all([
        dispatch(fetchWatchlistThunk({ skip: 0, limit: 50 })),
        symbols.length > 0 && dispatch(fetchBatchSnapshots(symbols))
      ]);
    } finally {
      setTimeout(() => setIsRefreshing(false), 1000);
    }
  };

  const handleRemove = (stockId) => {
    if (!userId) return;
    dispatch(unfollowStockThunk({ userId, stockId }))
      .unwrap()
      .catch(() => {/* errors surface via slice */});
  };

  // Filter and sort watchlist items
  const filteredAndSortedStocks = useMemo(() => {
    let filtered = watchlistItems.filter(stock => {
      const matchesSearch = stock.symbol.toLowerCase().includes(searchTerm.toLowerCase()) ||
                           stock.name.toLowerCase().includes(searchTerm.toLowerCase());
      return matchesSearch;
    });
    
    return filtered.sort((a, b) => {
      switch (sortBy) {
        case 'change':
          const aMarketData = batchSnapshots.find(snap => snap.ticker === a.symbol);
          const bMarketData = batchSnapshots.find(snap => snap.ticker === b.symbol);
          const aChange = aMarketData?.change ?? 0;
          const bChange = bMarketData?.change ?? 0;
          return bChange - aChange;
        default:
          return a.symbol.localeCompare(b.symbol);
      }
    });
  }, [watchlistItems, searchTerm, sentimentFilter, sortBy, batchSnapshots]);

  // Reset showAll when filters change
  useEffect(() => {
    // Reset expanded stocks when timeframe changes
    setExpandedStocks(new Set());
  }, [timeframe]);

  // Show empty state if no watchlist items and not loading
  if (watchlistItems.length === 0 && !watchlistLoading) {
    return (
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-xl font-bold bg-gradient-to-r from-white via-gray-100 to-gray-300 bg-clip-text text-transparent">
              {t('watchlist.heading')}
            </h2>
            <p className="text-sm text-gray-400">{t('watchlist.subtitle')}</p>
          </div>
        </div>

        {/* Empty State */}
        <EmptyWatchlistState onExploreStocks={onExploreStocks} />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold bg-gradient-to-r from-gray-900 via-gray-800 to-gray-600 dark:from-white dark:via-gray-100 dark:to-gray-300 bg-clip-text text-transparent">
            {t('watchlist.heading')}
          </h2>
          <p className="text-sm text-gray-500 dark:text-gray-400">{t('watchlist.subtitle')}</p>
        </div>

        <div className="flex items-center gap-2">
          {/* Timeframe selector */}
          {timeframeOptions.map((option) => (
            <button
              key={option.value}
              onClick={() => setTimeframe(option.value)}
              className={`px-3 py-1.5 text-xs font-medium rounded-lg border transition-all duration-200 backdrop-blur-sm ${
                timeframe === option.value
                  ? "bg-blue-600/80 border-blue-500/60 text-white shadow-md"
                  : "bg-gray-100 border-gray-300 text-gray-600 hover:bg-gray-200 hover:text-gray-900 dark:bg-slate-800/60 dark:border-slate-600/50 dark:text-gray-400 dark:hover:bg-slate-700/60 dark:hover:text-white dark:hover:border-slate-500/60"
              }`}
              title={option.description}
            >
              {option.label}
            </button>
          ))}

          <button
            onClick={handleRefresh}
            disabled={isRefreshing}
            className="p-2 text-gray-500 hover:text-gray-900 dark:text-gray-400 dark:hover:text-white transition-colors disabled:opacity-50 hover:bg-gray-200 dark:hover:bg-slate-700/30 rounded-lg backdrop-blur-sm"
            title={t('watchlist.refresh')}
          >
            <RefreshCw className={`w-4 h-4 ${isRefreshing ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </div>
      
      {/* Search */}
      {watchlistItems.length > 3 && (
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
          <input
            type="text"
            placeholder={t('watchlist.searchPlaceholder')}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-3 bg-white dark:bg-slate-800/60 backdrop-blur-sm border border-gray-300 dark:border-slate-600/50 rounded-xl text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:border-blue-500/60 focus:bg-gray-50 dark:focus:bg-slate-700/60 text-sm transition-all duration-200"
          />
        </div>
      )}

      {/* Error State */}
      {watchlistError && (
        <div className="bg-red-50 dark:bg-red-900/20 backdrop-blur-sm border border-red-300 dark:border-red-600/40 rounded-xl p-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 bg-red-100 dark:bg-red-500/20 rounded-lg flex items-center justify-center">
              <AlertTriangle className="w-4 h-4 text-red-600 dark:text-red-400" />
            </div>
            <span className="text-sm text-red-700 dark:text-red-300">{watchlistError}</span>
          </div>
          <button
            onClick={reloadWatchlist}
            className="px-4 py-2 bg-red-100 hover:bg-red-200 text-red-700 dark:bg-red-600/20 dark:hover:bg-red-600/30 dark:text-red-300 rounded-lg text-sm transition-colors backdrop-blur-sm border border-red-300 dark:border-red-500/30"
          >
            {t('watchlist.retry')}
          </button>
        </div>
      )}
      
      {/* Loading State -- matches StockCard's fully-loaded shape (header,
          sentiment pill + chevron, bull/bear chips, stat chips, confidence
          row) rather than just its header, so the swap-in doesn't grow the
          card underneath the user's eyes. */}
      {(watchlistLoading || batchLoading) && (
        <div className="space-y-4">
          {[1, 2, 3].map(i => (
            <div key={i} className="bg-gray-100 dark:bg-slate-800/60 backdrop-blur-sm border border-gray-200 dark:border-slate-600/50 rounded-xl p-4 animate-pulse">
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-gray-300 dark:bg-slate-700/70 rounded-lg"></div>
                  <div className="space-y-2">
                    <div className="h-3 bg-gray-300 dark:bg-slate-700/70 rounded w-16"></div>
                    <div className="h-2 bg-gray-200 dark:bg-slate-700/70 rounded w-24"></div>
                  </div>
                </div>
                <div className="space-y-2 text-right">
                  <div className="h-3 bg-gray-300 dark:bg-slate-700/70 rounded w-16 ml-auto"></div>
                  <div className="h-2 bg-gray-200 dark:bg-slate-700/70 rounded w-20 ml-auto"></div>
                </div>
              </div>
              <div className="flex items-center justify-between mb-3 pb-3 border-b border-gray-200 dark:border-slate-600/50">
                <div className="h-6 bg-gray-200 dark:bg-slate-700/60 rounded-full w-24"></div>
                <div className="w-4 h-4 bg-gray-200 dark:bg-slate-700/60 rounded"></div>
              </div>
              <div className="flex gap-2 mb-3">
                <div className="h-6 bg-gray-200 dark:bg-slate-700/50 rounded-full w-20"></div>
                <div className="h-6 bg-gray-200 dark:bg-slate-700/50 rounded-full w-20"></div>
              </div>
              <div className="flex flex-wrap gap-2 mb-3">
                <div className="h-7 bg-gray-200 dark:bg-slate-700/50 rounded-full w-24"></div>
                <div className="h-7 bg-gray-200 dark:bg-slate-700/50 rounded-full w-24"></div>
                <div className="h-7 bg-gray-200 dark:bg-slate-700/50 rounded-full w-20"></div>
              </div>
              <div className="flex items-center justify-between">
                <div className="h-3 bg-gray-200 dark:bg-slate-700/50 rounded w-20"></div>
                <div className="h-3 bg-gray-200 dark:bg-slate-700/50 rounded w-16"></div>
              </div>
            </div>
          ))}
        </div>
      )}
      
      {/* Stock Cards Grid */}
      {!watchlistLoading && filteredAndSortedStocks.length > 0 && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div className="text-sm text-gray-500 dark:text-gray-400">
              {t('watchlist.showingCount', { shown: filteredAndSortedStocks.length, total: watchlistItems.length })}
            </div>

            {/* Sort options */}
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              aria-label={t('watchlist.sortAriaLabel')}
              className="px-3 py-1.5 bg-white dark:bg-slate-800/60 backdrop-blur-sm border border-gray-300 dark:border-slate-600/50 rounded-lg text-gray-900 dark:text-white text-sm focus:outline-none focus:border-blue-500/60 transition-all duration-200"
            >
              <option value="symbol">{t('watchlist.sortBy.symbol')}</option>
              <option value="change">{t('watchlist.sortBy.change')}</option>
            </select>
          </div>
          
          <div className="grid gap-4">
            {filteredAndSortedStocks.map(stock => (
              <StockCard
                key={stock.id}
                stock={stock}
                timeframe={timeframe}
                isExpanded={expandedStocks.has(stock.symbol)}
                onToggleExpanded={handleToggleExpanded}
                onRemove={handleRemove}
              />
            ))}
          </div>
        </div>
      )}
      
      {/* No Results */}
      {!watchlistLoading && filteredAndSortedStocks.length === 0 && watchlistItems.length > 0 && (
        <div className="text-center py-8">
          <div className="w-16 h-16 bg-gray-200 dark:bg-slate-700/30 backdrop-blur-sm rounded-xl flex items-center justify-center mx-auto mb-4">
            <Search className="w-6 h-6 text-gray-500 dark:text-gray-400" />
          </div>
          <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">{t('watchlist.noResults.title')}</h3>
          <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">{t('watchlist.noResults.subtitle')}</p>
          <button
            onClick={() => setSearchTerm('')}
            className="px-4 py-2 bg-gray-200 hover:bg-gray-300 text-gray-700 dark:bg-slate-700/60 dark:hover:bg-slate-600/60 dark:text-gray-300 rounded-lg text-sm transition-colors backdrop-blur-sm border border-gray-300 dark:border-slate-600/50"
          >
            {t('watchlist.noResults.clearSearch')}
          </button>
        </div>
      )}
    </div>
  );
};

export default SentimentDashboard;